import { stateWord } from "@lafayette/domain";
import type { StaffItineraryRow } from "@lafayette/schemas";
import { useState } from "react";
import { Link } from "react-router";
import {
  emptyNoteClass,
  hintClass,
  linkClass,
  listClass,
  SubHeading,
} from "../../requests/components/parts";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card } from "../../ui/card";
import { Icon } from "../../ui/icon";
import { Notice } from "../../ui/notice";
import { Panel } from "../../ui/panel";
import { lookOf, StatusLook } from "../../ui/status-look";
import {
  EVIDENCE_STATE_LABELS,
  FEASIBILITY_LABELS,
  issueWords,
  PLAN_STATUS_LOOK,
  SEVERITY_LABELS,
} from "../labels";
import { formatDay, localPartsAt } from "../time";
import {
  ACKNOWLEDGEABLE_CODE,
  ageText,
  currentReport,
  type Detail,
  detailsMissing,
  type EvidencePart,
  type EvidenceView,
  keyedIssues,
  type RunView,
} from "../view-model";
import { issueClass } from "./Timeline";

// The side panels: the checks (validation problems, evidence freshness), the revision list and the change history. They sit beside
// the timeline so a long plan never pushes them out of reach.

const utcText = (iso: string): string => {
  const parts = localPartsAt(Date.parse(iso), "UTC");
  return `${formatDay(parts.date)}, ${parts.time} UTC`;
};

function EvidenceLine({ label, part }: Readonly<{ label: string; part: EvidencePart }>) {
  const state = part.state === null ? null : EVIDENCE_STATE_LABELS[part.state];
  return (
    <li className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
      <strong>{label}:</strong>
      {state === null ? (
        <Badge>Nothing to check</Badge>
      ) : (
        <StatusLook look={state}>{state.word}</StatusLook>
      )}
      <span className="text-muted-foreground">{part.note}</span>
    </li>
  );
}

/** Closed reason codes of a run, in plain words. The code itself is never shown to the staff member as the explanation. */
const REASON_WORDS: Readonly<Record<string, string>> = {
  NOT_CONFIGURED: "the check is switched off on this server",
  TIMEOUT: "the service did not answer in time",
  QUOTA: "the service is over its limit for now",
  PROVIDER_UNAVAILABLE: "the service is not available",
  BUDGET: "the time for this check ran out",
  NO_OWNED_POINT: "the place has no point Lafayette owns",
  OUT_OF_RANGE: "the date is too far ahead",
  NO_ROUTE: "no route was found",
  INVALID_REQUEST: "the service could not use the places",
  LOCATION_UNKNOWN: "a place is missing",
  TRANSIT_OUT_OF_RANGE: "transit times are only read a week ahead",
};

const reasonWords = (code: string | null): string =>
  code === null ? "" : ` (${REASON_WORDS[code] ?? "no answer"})`;

function RunPart({ label, part }: Readonly<{ label: string; part: RunView["evidence"]["route"] }>) {
  const state =
    part.state === "VERIFIED"
      ? "verified"
      : part.state === "UNVERIFIED"
        ? "not verified"
        : "nothing to check";
  return (
    <li>
      <strong>{label}:</strong> {state}
      {reasonWords(part.reason)}
      {part.observedAt === null ? "" : `, read ${utcText(part.observedAt)}`}.
    </li>
  );
}

type ChecksProps = Readonly<{
  detail: Detail;
  evidence: EvidenceView;
  /** The answer of the last full validation run of THIS version of the plan, or `null`. */
  run: RunView | null;
  canValidate: boolean;
  validating: boolean;
  onValidate: (mode: "FULL" | "STRUCTURAL") => void;
}>;

export function ChecksPanel({
  detail,
  evidence,
  run,
  canValidate,
  validating,
  onValidate,
}: ChecksProps) {
  const report = currentReport(detail);
  const structural = new Map<string, number>();
  for (const issue of detail.issues)
    structural.set(issue.code, (structural.get(issue.code) ?? 0) + 1);
  // What the newest report counts beyond the structural issues listed above: the evidence of the gates.
  const evidenceEntries = (report?.issueCodes ?? [])
    .map((entry) => ({ ...entry, count: entry.count - (structural.get(entry.code) ?? 0) }))
    .filter((entry) => entry.count > 0);
  const sorted = [...detail.issues].sort((a, b) =>
    a.severity === b.severity ? 0 : a.severity === "BLOCKING" ? -1 : 1,
  );
  const feasibility = detail.report?.feasibility ?? "UNKNOWN";
  // Only the issues that really carry the acknowledgement count (review F2): the report lists the items whose gap staff acknowledged.
  const acknowledgedGaps = new Set(
    report?.issueCodes.find((entry) => entry.code === ACKNOWLEDGEABLE_CODE)
      ?.acknowledgedStableIds ?? [],
  ).size;
  const missing = detailsMissing(detail);

  return (
    <Panel id="checks-heading" title="Checks">
      {canValidate ? (
        <>
          <div className="flex flex-wrap gap-2">
            <Button disabled={validating} onClick={() => onValidate("FULL")}>
              {validating ? "Checking…" : "Run full validation"}
            </Button>
            <Button
              variant="outline"
              disabled={validating}
              onClick={() => onValidate("STRUCTURAL")}
            >
              Run structural check
            </Button>
          </div>
          <p className={hintClass}>
            Full validation reads the stored structure and then asks for travel times and forecasts,
            where those checks are switched on. The structural check looks at the structure and the
            times only: it reads no routes or weather and leaves the evidence below as it was.
          </p>
        </>
      ) : null}

      <p className="m-0 text-sm">
        <strong>Travel (newest report):</strong> {FEASIBILITY_LABELS[feasibility] ?? feasibility}
      </p>
      {acknowledgedGaps > 0 ? (
        <Notice tone="warning">
          <p>
            Staff acknowledged the travel gap into {acknowledgedGaps}{" "}
            {acknowledgedGaps === 1 ? "item" : "items"} (marked on{" "}
            {acknowledgedGaps === 1 ? "it" : "each"}
            ). Any other gap is not acknowledged. The plan is still shown as infeasible.
          </p>
        </Notice>
      ) : null}
      {missing ? (
        <Notice tone="warning">
          <p>
            Details not available from this server version: the items show no evidence, linked
            request or catalog state until the server is updated.
          </p>
        </Notice>
      ) : null}

      <SubHeading>Structure and times (this version, checked now)</SubHeading>
      {sorted.length === 0 ? (
        <p className={emptyNoteClass}>No problems found in the structure and the times.</p>
      ) : (
        <ul
          className={`${listClass} gap-2 text-sm`}
          aria-label="Problems in the structure and the times"
        >
          {keyedIssues(sorted).map(({ key, issue }) => {
            const words = issueWords(issue.code);
            const severity = SEVERITY_LABELS[issue.severity];
            const target =
              issue.itemId !== null
                ? { href: `#item-${issue.itemId}`, text: "Go to the item" }
                : issue.sectionId !== null
                  ? { href: `#day-${issue.sectionId}`, text: "Go to the day" }
                  : null;
            return (
              <li key={key} className={issueClass}>
                <StatusLook look={severity}>{severity.word}</StatusLook>{" "}
                <strong>{words.title}.</strong> <span>{words.detail}</span>
                {target === null ? null : (
                  <>
                    {" "}
                    <a className={linkClass} href={target.href}>
                      {target.text}
                    </a>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <SubHeading>Route and weather evidence (stored readings of the items)</SubHeading>
      <ul className={`${listClass} gap-2 text-sm`} aria-label="Evidence freshness">
        <EvidenceLine label="Routes" part={evidence.route} />
        <EvidenceLine label="Weather" part={evidence.weather} />
      </ul>
      <p className={hintClass}>
        {evidence.observedAt === null || evidence.ageMinutes === null
          ? "Nothing was read for this plan yet."
          : `Newest reading: ${utcText(evidence.observedAt)} (${ageText(evidence.ageMinutes)}).`}{" "}
        {evidence.reportCheckedAt === null
          ? "No validation report yet."
          : `Newest report: ${utcText(evidence.reportCheckedAt)}${evidence.reportOlderVersion ? ", for an older version of the plan, so it is out of date" : ""}.`}
      </p>
      {run === null ? null : (
        <>
          <SubHeading>Last full validation (this version)</SubHeading>
          <ul className={`${listClass} gap-1 text-sm`} aria-label="Last full validation">
            {run.observedAt === null ? null : (
              <li>Oldest reading used: {utcText(run.observedAt)}.</li>
            )}
            <RunPart label="Routes" part={run.evidence.route} />
            <RunPart label="Weather" part={run.evidence.weather} />
            <li>
              Provider calls: {run.providerCalls.routes} for routes, {run.providerCalls.weather} for
              weather.
              {run.suggestionCount > 0
                ? ` ${run.suggestionCount} forecast ${run.suggestionCount === 1 ? "change" : "changes"} could be proposed.`
                : ""}
            </li>
            {run.conciergeReview.length === 0 ? null : (
              <li>
                {run.conciergeReview.length}{" "}
                {run.conciergeReview.length === 1 ? "item needs" : "items need"} a concierge review
                because of a severe forecast.
              </li>
            )}
          </ul>
        </>
      )}
      {evidenceEntries.length === 0 ? null : (
        <ul
          className={`${listClass} gap-2 text-sm`}
          aria-label="Evidence issues in the newest report"
        >
          {evidenceEntries.map((entry) => {
            const words = issueWords(entry.code);
            const severity = SEVERITY_LABELS[entry.severity];
            return (
              <li key={entry.code} className={issueClass}>
                <StatusLook look={severity}>{severity.word}</StatusLook>{" "}
                <strong>{words.title}</strong>
                {entry.count > 1 ? ` (${entry.count} times)` : ""}. <span>{words.detail}</span>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

// --- revisions ----------------------------------------------------------------------------------------------------------------

const SHOWN_REVISIONS = 5;

export function RevisionList({
  tripId,
  rows,
  currentId,
}: Readonly<{ tripId: string; rows: readonly StaffItineraryRow[]; currentId: string }>) {
  const [all, setAll] = useState(false);
  const newestFirst = [...rows].sort(
    (a, b) => b.revision - a.revision || (a.createdAt < b.createdAt ? 1 : -1),
  );
  const shown = all ? newestFirst : newestFirst.slice(0, SHOWN_REVISIONS);
  return (
    <Panel id="revisions-heading" title="Revisions">
      <ol className={`${listClass} gap-3`} aria-label="Revisions, newest first">
        {shown.map((row) => {
          const look = lookOf(PLAN_STATUS_LOOK, row.status);
          return (
            <li
              key={row.id}
              className={`grid gap-1 [&_p]:m-0 ${row.id === currentId ? "border-l-[3px] border-primary pl-2" : ""}`}
            >
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <Link
                  className={`${linkClass} inline-flex min-h-11 items-center compact:min-h-8`}
                  to={`/trips/${encodeURIComponent(tripId)}/itinerary/${encodeURIComponent(row.id)}`}
                  aria-current={row.id === currentId ? "page" : undefined}
                >
                  Revision {row.revision}
                  {row.status === "DRAFT" ? ` (draft v${row.version})` : ""}
                </Link>
                <StatusLook look={look}>
                  {stateWord("itinerary", "staffShort", row.status)}
                </StatusLook>
                {row.id === currentId ? (
                  <span className="text-sm text-muted-foreground">· you are reading this one</span>
                ) : null}
              </span>
              <p className={hintClass}>
                {row.createdBy === "STAFF" ? "Staff" : "Customer"}
                {row.activatedAt === null
                  ? ", not activated"
                  : `, activated ${utcText(row.activatedAt)}`}
              </p>
            </li>
          );
        })}
      </ol>
      {newestFirst.length > SHOWN_REVISIONS ? (
        <div>
          <Button variant="outline" onClick={() => setAll((value) => !value)}>
            {all ? "Show fewer revisions" : `Show all ${newestFirst.length} revisions`}
          </Button>
        </div>
      ) : null}
    </Panel>
  );
}

// --- change history -----------------------------------------------------------------------------------------------------------

const COMMAND_WORDS: Readonly<Record<string, string>> = {
  CREATE_PLAN: "Plan created",
  REPLACE_PLAN: "Plan replaced",
  EDIT_DRAFT: "Draft edited",
  PROPOSE_EDIT: "Proposal saved",
  APPLY_EDIT: "Edit applied",
  ACTIVATE: "Plan activated",
};

const APPROVAL_WORDS: Readonly<Record<string, string>> = {
  NONE: "no approval needed",
  CUSTOMER_CONFIRMED: "confirmed by the customer",
  STAFF: "approved by staff",
};

export function ChangeHistory({ changes }: Readonly<{ changes: Detail["changes"] }>) {
  if (changes.length === 0) return null;
  return (
    <section aria-labelledby="changes-heading">
      <Card>
        <details className="group p-4 sm:p-6">
          <summary className="flex min-h-11 cursor-pointer list-none items-center gap-1 compact:min-h-8 [&::-webkit-details-marker]:hidden">
            <Icon
              name="arrow-right-01-round"
              className="size-4 shrink-0 transition-transform group-open:rotate-90"
            />
            <h2 id="changes-heading" className="m-0 font-serif text-xl leading-7 font-medium">
              Change history ({changes.length})
            </h2>
          </summary>
          <ol
            className={`${listClass} mt-3 gap-2 text-sm`}
            aria-label="Change history, newest first"
          >
            {[...changes]
              .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
              .map((change) => (
                <li key={change.id}>
                  <strong>{COMMAND_WORDS[change.command] ?? change.command}</strong>{" "}
                  <span className="text-muted-foreground">
                    by {change.actor.kind}, {APPROVAL_WORDS[change.approval] ?? change.approval},{" "}
                    {utcText(change.createdAt)}
                  </span>
                </li>
              ))}
          </ol>
        </details>
      </Card>
    </section>
  );
}
